import { signal } from '@angular/core';
import { applyEach, debounce, FieldTree, min, required, SchemaPathTree } from '@angular/forms/signals';
import { ModelFaultSourceDto } from '../../../core/mceapi.client.generated';
import { VoltageCase } from '../system-voltage/system-voltage.model';


export interface FaultSourceValues {
  r: number
  x: number
  clearanceTime: number
}

export interface FaultSource {
  sourceType: VoltageCase;
  sources: FaultSourceValues[]; // 0 = short circuit, 1 = earth fault
}

function buildFaultSourceSchema(path: SchemaPathTree<FaultSourceValues>) {

  required(path.r, { message: `Fault resistance  is required` });
  min(path.r, 0, { message: `Fault resistance  must be above 0 m` });
  debounce(path.r, 500);

  required(path.x, { message: `Fault reactance  is required` });
  debounce(path.x, 500);

  required(path.clearanceTime, { message: `Fault clearance time  is required` });
  min(path.clearanceTime, 0.001, { message: `Fault clearance time  must be above 0.001` });
  debounce(path.clearanceTime, 500);

}

export function buildFaultSourcesSchema(path: SchemaPathTree<FaultSource>) {
  applyEach(path.sources,buildFaultSourceSchema);  
}

export function createFaultSource(dto: ModelFaultSourceDto) {
  if (dto.r === undefined || dto.x === undefined
    || dto.re === undefined || dto.xe === undefined
    || dto.shortCircuitT === undefined || dto.earthFaultT === undefined) {
    throw new Error('Fault source DTO is missing required values.');
  }

  return signal<FaultSource>({
    sourceType: dto.case as VoltageCase,
    sources: [
      {
        r: dto.r.roundTo(6),
        x: dto.x.roundTo(6),
        clearanceTime: dto.shortCircuitT
      },
      {
        r: dto.re.roundTo(6),
        x: dto.xe.roundTo(6),
        clearanceTime: dto.earthFaultT
      }
    ]
  });
}

export function createFaultSourceDto(model: FieldTree<FaultSource>) {
  const sources = Array.from(model.sources);
  const sc = sources[0];
  const ef = sources[1];
  return signal<ModelFaultSourceDto>({
    case: model.sourceType().value(),
    r: sc.r().value(),
    x: sc.x().value(),
    re: ef.r().value(),
    xe: ef.x().value(),
    shortCircuitT: sc.clearanceTime().value(),
    earthFaultT: ef.clearanceTime().value()
  });
}
